// The chip on a partially received roof item: its first gap, "+N" with more, and on hover
// why it is partial, what we hold and what is still needed. The grade's notes never show
// here: the Amazon overview is shared with Amazon (docs/Y2026-Q4/design-partially-received.md).
import type { NeedPartial } from "../api/hooks/useAmazonOverview";
import { formatDay } from "../utils/attributeFormat";
import { GAP_LABEL } from "./gaps";
import { Tooltip } from "./Tooltip";
import styles from "./GapTag.module.css";

const heldText = (p: NeedPartial): string =>
  p.held
    .map((h) => `${h.document || "a document on file"}${h.page ? `, page ${h.page}` : ""}`)
    .join("; ");

export const GapTag = ({
  partial,
  heading,
  flush = false,
}: {
  partial: NeedPartial;
  heading: string;
  /** On a line of its own, with no gap before it. */
  flush?: boolean;
}) => {
  const held = heldText(partial);
  // An operator's override to partially received carries no grade: nothing to show.
  if (!partial.gaps.length && !held && !partial.to_provide) return null;
  const [first, ...rest] = partial.gaps;
  // A grade from before gaps were recorded says so rather than showing nothing.
  const label = first ? GAP_LABEL[first.kind] : "What's still needed";
  return (
    <Tooltip
      content={
        <div className={styles.tip}>
          <div className={styles.tipHead}>{heading}</div>
          {partial.gaps.length > 0 && (
            <ul className={styles.tipList}>
              {partial.gaps.map((g, i) => (
                <li key={i}>
                  <b>{GAP_LABEL[g.kind]}:</b> {g.detail}
                </li>
              ))}
            </ul>
          )}
          {held && (
            <div>
              <span className={styles.tipKey}>We hold:</span> {held}
            </div>
          )}
          {partial.to_provide && (
            <div>
              <span className={styles.tipKey}>Still needed:</span> {partial.to_provide}
            </div>
          )}
          {partial.graded_on && (
            <div className={styles.tipSub}>Graded {formatDay(partial.graded_on)}</div>
          )}
        </div>
      }
    >
      <span className={flush ? `${styles.gapTag} ${styles.flush}` : styles.gapTag} tabIndex={0}>
        {label}
        {rest.length > 0 && <span className={styles.more}> +{rest.length}</span>}
      </span>
    </Tooltip>
  );
};
